import { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Button, Alert,
  CircularProgress, FormControl, InputLabel, Select, MenuItem,
  Autocomplete, Stack, Divider
} from '@mui/material';
import { Refresh, FilterList } from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function KardexProducto() {
  const [productos, setProductos] = useState([]);
  const [productoSeleccionado, setProductoSeleccionado] = useState(null);
  const [bodegas, setBodegas] = useState([]);
  const [bodegaId, setBodegaId] = useState('');
  const [movimientos, setMovimientos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('');

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => { cargarProductos(); cargarBodegas(); }, []);
  useEffect(() => { if (productoSeleccionado) cargarKardex(); }, [productoSeleccionado, bodegaId]);

  const cargarProductos = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/productos?tipo=PRODUCTO`, { headers: headers() });
      setProductos(Array.isArray(res.data) ? res.data : []);
    } catch { setProductos([]); }
  };

  const cargarBodegas = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/bodegas`, { headers: headers() });
      setBodegas(res.data?.data || []);
    } catch { setBodegas([]); }
  };

  const cargarKardex = async () => {
    if (!productoSeleccionado) return;
    setLoading(true);
    try {
      let url = `${API_URL}/api/kardex-inventario/producto/${productoSeleccionado.id}`;
      const params = new URLSearchParams();
      if (bodegaId) params.append('bodegaId', bodegaId);
      if (params.toString()) url += `?${params}`;
      const res = await axios.get(url, { headers: headers() });
      let movs = res.data?.data || [];
      if (fechaDesde) movs = movs.filter(m => m.fecha >= fechaDesde);
      if (fechaHasta) movs = movs.filter(m => m.fecha <= fechaHasta + 'T23:59:59');
      if (tipoFiltro) movs = movs.filter(m => m.tipo === tipoFiltro);
      setMovimientos(movs);
    } catch { setMovimientos([]); }
    setLoading(false);
  };

  const fmt = (v) => `$${parseFloat(v || 0).toFixed(2)}`;
  const fmtFecha = (f) => f ? new Date(f).toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

  const chipTipo = (tipo) => {
    const colores = { ENTRADA: 'success', SALIDA: 'error', AJUSTE: 'warning', TRANSFERENCIA: 'info' };
    return <Chip label={tipo} size="small" color={colores[tipo] || 'default'} />;
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>📒 Kardex por Producto</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Consulte el historial completo de movimientos de inventario de un producto específico.
        </Typography>

        {/* Selector de producto */}
        <Autocomplete
          options={productos}
          getOptionLabel={(o) => `${o.codigo || 'S/C'} - ${o.nombre}`}
          value={productoSeleccionado}
          onChange={(_, v) => setProductoSeleccionado(v)}
          renderInput={(params) => <TextField {...params} label="Seleccionar Producto *" placeholder="Busque por código o nombre..." />}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          sx={{ mb: 2 }}
        />

        {/* Filtros */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>Bodega</InputLabel>
            <Select value={bodegaId} label="Bodega" onChange={(e) => setBodegaId(e.target.value)}>
              <MenuItem value="">Todas</MenuItem>
              {bodegas.map(b => <MenuItem key={b.id} value={b.id}>{b.nombre}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 160 }}>
            <InputLabel>Tipo</InputLabel>
            <Select value={tipoFiltro} label="Tipo" onChange={(e) => setTipoFiltro(e.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="ENTRADA">Entrada</MenuItem>
              <MenuItem value="SALIDA">Salida</MenuItem>
              <MenuItem value="AJUSTE">Ajuste</MenuItem>
              <MenuItem value="TRANSFERENCIA">Transferencia</MenuItem>
            </Select>
          </FormControl>
          <TextField type="date" label="Desde" InputLabelProps={{ shrink: true }} sx={{ width: 160 }}
            value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} />
          <TextField type="date" label="Hasta" InputLabelProps={{ shrink: true }} sx={{ width: 160 }}
            value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} />
          <Button variant="outlined" startIcon={<FilterList />} onClick={cargarKardex} sx={{ whiteSpace: 'nowrap' }}>Filtrar</Button>
        </Stack>

        {!productoSeleccionado ? (
          <Alert severity="info">Seleccione un producto para ver su kardex.</Alert>
        ) : loading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : movimientos.length === 0 ? (
          <Alert severity="warning">No hay movimientos registrados para este producto.</Alert>
        ) : (
          <>
            {/* Resumen del producto */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#fafafa' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Código</Typography>
                  <Typography fontWeight={600}>{productoSeleccionado.codigo || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Producto</Typography>
                  <Typography fontWeight={600}>{productoSeleccionado.nombre}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Stock Actual</Typography>
                  <Typography fontWeight={700} color="primary.main" variant="h6">
                    {parseFloat(productoSeleccionado.cantidad_stock || productoSeleccionado.stock_actual || 0)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Costo Promedio</Typography>
                  <Typography fontWeight={600}>
                    {movimientos.length > 0 ? fmt(movimientos[movimientos.length - 1].saldo?.costoUnitario) : '—'}
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            {/* Tabla de movimientos */}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#1a237e', '& th': { color: '#fff', fontWeight: 'bold', py: 1.2 } }}>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Referencia</TableCell>
                    <TableCell>Bodega</TableCell>
                    <TableCell align="right">Cantidad</TableCell>
                    <TableCell align="right">C. Unit.</TableCell>
                    <TableCell align="right">C. Total</TableCell>
                    <TableCell align="right">Saldo</TableCell>
                    <TableCell align="right">Saldo $</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {movimientos.map((m) => (
                    <TableRow key={m.id} hover>
                      <TableCell>{fmtFecha(m.fecha)}</TableCell>
                      <TableCell>{chipTipo(m.tipo)}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{m.referencia?.tipo || '—'}</Typography>
                      </TableCell>
                      <TableCell>{m.bodega?.nombre || '—'}</TableCell>
                      <TableCell align="right" sx={{ color: m.tipo === 'SALIDA' || m.tipo === 'TRANSFERENCIA' ? 'error.main' : 'success.main', fontWeight: 600 }}>
                        {m.tipo === 'SALIDA' || m.tipo === 'TRANSFERENCIA' ? '-' : '+'}{m.cantidad}
                      </TableCell>
                      <TableCell align="right">{fmt(m.costoUnitario)}</TableCell>
                      <TableCell align="right">{fmt(m.costoTotal)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{m.saldo?.cantidad ?? '—'}</TableCell>
                      <TableCell align="right">{fmt(m.saldo?.costoTotal)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Total: {movimientos.length} movimientos
            </Typography>
          </>
        )}
      </Paper>
    </Box>
  );
}
